import {
  BoxV2 as Box,
  ButtonV2 as Button,
  DropdownV3 as Dropdown,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Role, User } from '@/apis/core/types';
import {
  ModalType,
  UserCreatePayload,
  UserUpdatePayload,
} from '@/applications/CspPortal/Profile/types';
import {
  isRequired,
  isValidEmail,
  isValidName,
} from '@/applications/CspPortal/Profile/validators';
import { roleEnumToLabel } from '@/applications/CspPortal/Profile/utils';
import Modal from '../Modal';

type UserEditModalProps = {
  open: boolean;
  mode: ModalType;
  user?: User;
  roles: string[];
  processing: boolean;
  onClose: () => void;
  onSubmit: (data: UserCreatePayload | UserUpdatePayload | User) => void;
  dataTestId?: string;
};

const UserEditModal: FunctionComponent<UserEditModalProps> = ({
  open,
  mode,
  user,
  roles,
  processing,
  onClose,
  onSubmit,
  dataTestId,
}) => {
  const form = useForm<UserCreatePayload | UserUpdatePayload>({
    mode: 'onChange',
  });
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isDirty },
  } = form;

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        label: roleEnumToLabel(role),
        value: role,
      })),
    [roles]
  );

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && user) {
        reset({
          userAppRole: user.profile.csp_app_role?.[0] ?? Role.Viewer,
        });
      } else if (mode === 'add') {
        reset({
          firstName: '',
          lastName: '',
          email: '',
          userAppRole: '',
        });
      }
    }
  }, [open, mode, user, reset]);

  const renderTitle = () => {
    if (mode === 'add') return 'New User';
    if (mode === 'edit') return 'Edit User';
    if (mode === 'delete')
      return `Do you want to permanently delete user, ${user?.profile.fullName}?`;
    return '';
  };

  const renderDescription = () => {
    if (mode === 'add') return '* All fields required.';
    if (mode === 'edit' || mode === 'delete')
      return `USER ID: ${user?.id || 'N/A'}`;
    return null;
  };

  const getSubmitButtonText = () => {
    if (mode === 'delete') return 'Delete User';
    return 'Save';
  };

  const onFormSubmit = (formData: UserCreatePayload | UserUpdatePayload) => {
    if (mode === 'delete') {
      if (user) {
        onSubmit(user);
      }
    } else {
      onSubmit(formData);
    }
  };

  return (
    <Modal open={open} onClose={onClose} data-testid={dataTestId}>
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
          style={{ width: '100%' }}
          data-testid="userForm"
        >
          <Flex sx={{ flexDirection: 'column', gap: 24, width: '100%' }}>
            {/* Modal Header: title, description */}
            <Flex
              sx={{
                flexDirection: 'column',
                gap: 8,
                color: '#19262A',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'left',
              }}
            >
              <Box as="h2" sx={{ fontSize: '24px', fontWeight: '400' }}>
                {renderTitle()}
              </Box>
              <Box sx={{ fontSize: '14px', color: '#4D5659' }}>
                {renderDescription()}
              </Box>
            </Flex>

            <Box sx={{ backgroundColor: '#CCCFD0', height: 1 }} />

            {/* Body */}
            {(mode === 'add' || mode === 'edit') && (
              <Flex
                sx={{ flexDirection: 'column', gap: '24px', width: '100%' }}
              >
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue={mode === 'edit' ? user?.profile.firstName : ''}
                  rules={{ validate: { isRequired, isValidName } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      {...field}
                      required
                      label="First Name"
                      readOnly={mode === 'edit'}
                      disabled={processing}
                      error={!!error}
                      note={error?.message}
                      data-testid="firstNameInput"
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue={mode === 'edit' ? user?.profile.lastName : ''}
                  rules={{ validate: { isRequired, isValidName } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      {...field}
                      required
                      label="Last Name"
                      readOnly={mode === 'edit'}
                      disabled={processing}
                      error={!!error}
                      note={error?.message}
                      data-testid="lastNameInput"
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  defaultValue={mode === 'edit' ? user?.profile.email : ''}
                  rules={{ validate: { isRequired, isValidEmail } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      {...field}
                      required
                      label="Email Address"
                      readOnly={mode === 'edit'}
                      disabled={processing}
                      error={!!error}
                      note={error?.message}
                      data-testid="emailInput"
                    />
                  )}
                />
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="userAppRole"
                    control={control}
                    rules={{ validate: { isRequired } }}
                    render={({ field, fieldState: { error } }) => (
                      <Dropdown
                        {...field}
                        required
                        label="Role"
                        options={roleOptions}
                        disabled={processing}
                        error={!!error}
                        note={error?.message}
                        data-testid="roleDropdown"
                      />
                    )}
                  />
                </Box>
              </Flex>
            )}

            {mode !== 'delete' && (
              <Box sx={{ backgroundColor: '#CCCFD0', height: 1 }} />
            )}

            {/* Footer Buttons */}
            <Flex sx={{ justifyContent: 'flex-end', gap: '16px' }}>
              <Button
                variant="outline"
                shape="square"
                size="lg"
                color="secondary"
                onClick={onClose}
                data-testid="cancelButton"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                shape="square"
                size="lg"
                color="secondary"
                disabled={
                  processing || (mode !== 'delete' && (!isValid || !isDirty))
                }
                data-testid="submitButton"
              >
                {getSubmitButtonText()}
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default UserEditModal;
